/**
 * Headline graphic for the model-shootout post.
 *
 * Two panels with a shared y-axis (60–100) make the spread contrast visually
 * obvious: one-shot bars cluster within ~2.4 pts, turn-based bars span ~21 pts.
 * Drawn as an inline SVG so it inherits the site's color tokens via Tailwind
 * `text-*` classes mapped through `currentColor` (no chart library, no PNG).
 */

type Bar = {
  label: string;
  score: number;
  /** Provider color hint — uses Tailwind palette via class. */
  family: 'claude' | 'gpt' | 'gemini' | 'open';
};

// 2026-07 refresh: same five current-frontier models in both panels, all scored
// by one judge (Opus 4.7). See runs/plan-optimizer_20260704_sonnet5/ (one-shot)
// and runs/_rebaseline_20260704/ (turn-based, common 6 scenarios).
const ONE_SHOT: Bar[] = [
  { label: 'Opus 4.8', score: 95.0, family: 'claude' },
  { label: 'Fable 5', score: 96.0, family: 'claude' },
  { label: 'Sonnet 5', score: 92.8, family: 'claude' },
  { label: 'GPT 5.5', score: 91.0, family: 'gpt' },
  { label: 'Gemini 3.1', score: 92.2, family: 'gemini' },
];

const TURN_BASED: Bar[] = [
  { label: 'Opus 4.8', score: 92.3, family: 'claude' },
  { label: 'Fable 5', score: 92.2, family: 'claude' },
  { label: 'Sonnet 5', score: 89.0, family: 'claude' },
  { label: 'GPT 5.5', score: 87.2, family: 'gpt' },
  { label: 'DeepSeek', score: 75.0, family: 'open' },
  { label: 'Gemini 3.1', score: 70.7, family: 'gemini' },
  { label: 'Kimi K2.6', score: 45.8, family: 'open' },
];

// "DeepSeek" bar = DeepSeek V4 Pro (label shortened so 7 bars fit); full name is in the post body.

const Y_MIN = 40;
const Y_MAX = 100;
const Y_TICKS = [40, 50, 60, 70, 80, 90, 100];

const FAMILY_FILL: Record<Bar['family'], string> = {
  claude: '#c87f5a',
  gpt: '#3a8a6b',
  gemini: '#5a7fc8',
  open: '#9a5ac8',
};

type PanelProps = {
  title: string;
  spread: string;
  data: Bar[];
  /** Width in SVG user units. */
  width: number;
  height: number;
  showYAxis: boolean;
};

const Panel = ({ title, spread, data, width, height, showYAxis }: PanelProps) => {
  const padTop = 28;
  const padBottom = 56;
  const padLeft = showYAxis ? 36 : 12;
  const padRight = 12;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;

  const yFor = (score: number) =>
    padTop + plotH - ((score - Y_MIN) / (Y_MAX - Y_MIN)) * plotH;

  const barGap = 12;
  const barW = (plotW - barGap * (data.length - 1)) / data.length;

  return (
    <g>
      {/* Panel title */}
      <text
        x={padLeft + plotW / 2}
        y={16}
        textAnchor="middle"
        className="fill-text-primary text-[13px] font-semibold"
      >
        {title}
      </text>

      {/* Y-axis ticks + gridlines */}
      {Y_TICKS.map((tick) => {
        const y = yFor(tick);
        return (
          <g key={tick}>
            <line
              x1={padLeft}
              x2={padLeft + plotW}
              y1={y}
              y2={y}
              className="stroke-border"
              strokeWidth={1}
              strokeDasharray={tick === Y_MIN ? '0' : '2 3'}
            />
            {showYAxis ? (
              <text
                x={padLeft - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-text-tertiary text-[10px]"
              >
                {tick}
              </text>
            ) : null}
          </g>
        );
      })}

      {/* Bars */}
      {data.map((bar, i) => {
        const x = padLeft + i * (barW + barGap);
        const y = yFor(bar.score);
        const h = padTop + plotH - y;
        return (
          <g key={`${bar.label}-${i}`}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={3}
              fill={FAMILY_FILL[bar.family]}
            />
            <text
              x={x + barW / 2}
              y={y - 5}
              textAnchor="middle"
              className="fill-text-primary text-[10px] font-medium"
            >
              {bar.score.toFixed(1)}
            </text>
            <text
              x={x + barW / 2}
              y={padTop + plotH + 14}
              textAnchor="middle"
              className="fill-text-secondary text-[10px]"
            >
              {bar.label}
            </text>
          </g>
        );
      })}

      {/* Spread annotation */}
      <text
        x={padLeft + plotW / 2}
        y={height - 8}
        textAnchor="middle"
        className="fill-text-secondary text-[11px] italic"
      >
        {spread}
      </text>
    </g>
  );
};

export const EvalSpreadChart = () => {
  const totalWidth = 720;
  const height = 320;
  // One-shot has 5 bars, turn-based has 7; split proportionally so bar widths match.
  const leftW = Math.round(totalWidth * (5 / 12));
  const rightW = totalWidth - leftW;

  return (
    <figure className="my-6">
      <div className="rounded-panel border border-border bg-surface p-4">
        <svg
          viewBox={`0 0 ${totalWidth} ${height}`}
          role="img"
          aria-labelledby="eval-spread-title eval-spread-desc"
          className="w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <title id="eval-spread-title">
            One-shot vs turn-based eval scores across five frontier LLMs
          </title>
          <desc id="eval-spread-desc">
            One-shot plan generation scores cluster within about five points across
            five current-frontier models; turn-based elicitation scores span more than
            forty points across seven models including two open-weight models, with
            Opus 4.8 and Fable 5 leading and Kimi K2.6 at the bottom.
          </desc>
          <Panel
            title="One-shot plan generation"
            spread="5 models within 5 pts"
            data={ONE_SHOT}
            width={leftW}
            height={height}
            showYAxis
          />
          <g transform={`translate(${leftW}, 0)`}>
            <line
              x1={0}
              x2={0}
              y1={20}
              y2={height - 40}
              className="stroke-border"
              strokeWidth={1}
            />
            <Panel
              title="Turn-based elicitation"
              spread="7 models span ~46 pts"
              data={TURN_BASED}
              width={rightW}
              height={height}
              showYAxis={false}
            />
          </g>
        </svg>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-text-secondary">
          {(['claude', 'gpt', 'gemini', 'open'] as const).map((family) => (
            <span key={family} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: FAMILY_FILL[family] }}
              />
              <span className="capitalize">
                {family === 'gpt' ? 'GPT' : family === 'open' ? 'Open-weight' : family}
              </span>
            </span>
          ))}
        </div>
      </div>
      <figcaption className="mt-2 text-center text-xs text-text-tertiary">
        Judge scores on a 100-point rubric.
      </figcaption>
    </figure>
  );
};
